import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { useListSpaces } from 'src/features/spaces/api/useListSpaces';
import CreateSpaceFormModal from 'src/features/spaces/components/form/CreateSpaceFormModal';
import CellSpaceActions from 'src/features/spaces/components/table/CellSpaceActions';
import { disallowedVisitorTranslationMap, spaceTypeTranslationMap } from 'src/features/spaces/utils/constants';
import { spacesMessages } from 'src/features/spaces/utils/messages';
import { Space } from 'src/features/spaces/utils/types';
import { useListZones } from 'src/features/zone/api/useListZones';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';

function Spaces() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const manageSpaces = useHasPermissions([PERMISSION.MANAGE_SPACES]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'number' });
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'number', desc: false }] });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions, parkingId],
    );

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(spacesMessages.spaces));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: spacesData, isLoading, isError } = useListSpaces(payload);
    const { data: zonesData } = useListZones({ parkingId });

    const columnHelper = createColumnHelper<Space>();

    const columns = [
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(spacesMessages.number),
            },
        }),
        columnHelper.accessor('zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) => {
                const entryZoneId = row.original.zone;
                const zone = zonesData?.results.find((zone) => zone.id === entryZoneId);

                return zone?.translations.find((translation) => translation.language === locale)?.name || '-';
            },
            meta: {
                title: formatMessage(spacesMessages.zone),
            },
            minSize: 150,
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) => {
                return formatMessage(spaceTypeTranslationMap[row.original.type]);
            },
            meta: {
                title: formatMessage(spacesMessages.type),
            },
            minSize: 150,
        }),
        columnHelper.accessor('is_free', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(spacesMessages.isFree),
            },
        }),
        columnHelper.accessor('is_reserved', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(spacesMessages.isReserved),
            },
        }),
        columnHelper.accessor('disallow_usage_for', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.disallow_usage_for
                    ? formatMessage(disallowedVisitorTranslationMap[row.original.disallow_usage_for])
                    : '-';
            },
            meta: {
                title: formatMessage(spacesMessages.disallowedVisitorCategories),
            },
        }),
        ...(manageSpaces
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellSpaceActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<Space, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={spacesData}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(spacesMessages.spaces),
                entryName: formatMessage(spacesMessages.space).toLowerCase(),
                entryNames: formatMessage(spacesMessages.spaces).toLowerCase(),
                placeholder: formatMessage(spacesMessages.number),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageSpaces && { newEntry: CreateSpaceFormModal }),
            }}
        />
    );
}

export default Spaces;
